import React, { useState, useEffect, useRef} from 'react'
import { Button, IconView16, CircularLoader, IconClock16,
          IconCheckmark16, IconInfo16, colors
         } from '@dhis2/ui'
import { useDataEngine, useDataMutation } from '@dhis2/app-runtime';
import { useAuth } from '../auth/AuthContext';
import { useMyObjectivesContext } from './MyObjectivessPage'

const roleRanks = {
    R3lFolCMdoi: 0, // Admin
    nLSSLch316I: 1, // CEO
    adeqQmxfCLF: 2, // HOD
    gtMJNK0QVL7: 3, // Top Staff
    vdVjNaqxblC: 4, // Senior Staff
    Guest: 99,
};

const MyObjectivesList = ({ 
   isCEO = false, 
   isHOD = false, 
   isTopStaff = false, 
   isSeniorStaff = false, 
   reportMode = false,
   title = "My Objectives List" 
 }) => {
   const { user } = useAuth(); 
   const engine = useDataEngine();  
   const { events = [], setLoading, loadingEvents } = useMyObjectivesContext()
   const [objectivesWithRoles, setObjectivesWithRoles] = useState([]);
   const [expandedObjectives, setExpandedObjectives] = useState(new Set()); 
   const [localLoading, setLocalLoading] = useState(false); // Add local loading state
   const userRoleCache = useRef(new Map())
   
   const loadingTimerRef = useRef(null);
    
 const normalizedLoggedInUser = user.username.toLowerCase().trim();
   
 const myObjectives = objectivesWithRoles.filter(obj =>
    obj.username?.toLowerCase() === normalizedLoggedInUser
  )

  const objectiveIdsKey = myObjectives.map((objective) => objective.id).join('|')

   const toggleExpand = (objectiveId) => {
        if (reportMode) return
        setExpandedObjectives(prev => {
            const newSet = new Set(prev)
            if (newSet.has(objectiveId)) newSet.delete(objectiveId)
            else newSet.add(objectiveId)
            return newSet
        })
    }

  useEffect(() => {
    if (!reportMode) return
    setExpandedObjectives(new Set(myObjectives.map((objective) => objective.id)))
  }, [reportMode, objectiveIdsKey])

  const fetchUserRole = async (username) => {
    if (!username) return null
    if (userRoleCache.current.has(username)) {
      return userRoleCache.current.get(username)
    }

    try {
      const { users } = await engine.query({
        users: {
          resource: 'users',
          params: {
            filter: `username:eq:${username}`,
            fields: 'id,userCredentials[userRoles[id,name]]',
          },
        },
      })

      const role = users?.users?.[0]?.userCredentials?.userRoles?.[0]?.id || null
      userRoleCache.current.set(username, role)
      return role
    } catch (error) {
      console.error('Error fetching user role:', error)
      userRoleCache.current.set(username, null)
      return null
    }
  }

  const transformEventToObjective = async (event) => {
  try {
    // ✅ ADD THIS FILTER - EXCLUDE TASK EVENTS
    const isTaskEvent = event.programStage === 'Nv9nYNcBTdM'; // Task program stage ID
    
    if (isTaskEvent) {
      console.log('🔍 Filtering out task event from objectives:', event.event);
      return null; // Skip processing task events
    }

    // ✅ Keep your existing KRA/KPI filters
    const isKraEvent = event.programStage === 'BJb3qTgbcTF';
    const isKpiEvent = event.programStage === 'ivyCLjNLFrd';
    
    if (isKraEvent || isKpiEvent) {
      console.log('🔍 Filtering out KRA/KPI event from objectives:', event.event);
      return null; // Skip processing KRA/KPI events
    }

    /////////////////////THIS MY BE WRONG WHEN RENDERING OBJECTIVES BASED ON POSITION////////////////
    const usernameRaw = event.createdByUserInfo?.username || event.storedBy || event.lastUpdatedByUserInfo?.username || null;
    const username = usernameRaw ? usernameRaw.toLowerCase().trim() : null;
    /////////////////////\
     
    if (!username) return null;
       
    const userRole = await fetchUserRole(username);

    console.log('🔍 Main objective event:', event.event);
    console.log('🔍 Drag & Drop values:', {
      objectiveAlignment: event.dataValues?.find(dv => dv.dataElement === 'neuilg3L4d2')?.value,
      objectiveRoleAssigned: event.dataValues?.find(dv => dv.dataElement === 'vFoMLF5eZ5X')?.value,
      higherLevelObjectiveId: event.dataValues?.find(dv => dv.dataElement === 'plJovUlxfQ0')?.value
    });
    
    // ✅ FETCH LINKED KRAs AND KPIs
    const kras = await fetchLinkedEvents(event.event, 'kra');
    const kpis = await fetchLinkedEvents(event.event, 'kpi');

    console.log('🔍 Linked KRAs:', kras);
    console.log('🔍 Linked KPIs:', kpis);

    console.log('🔍 [TRANSFORM DEBUG] Processing event:', {
      eventId: event.event,
      programStage: event.programStage,
      isTaskEvent,
      isKraEvent,
      isKpiEvent,
      shouldFilter: isTaskEvent || isKraEvent || isKpiEvent
    });

    if (isTaskEvent || isKraEvent || isKpiEvent) {
      console.log('✅ Filtering out task/KRA/KPI event from objectives:', event.event);
      return null; // Skip processing task/KRA/KPI events
    }

    // ✅ ADDITIONAL SAFETY CHECK: Look for task data elements
    const hasTaskData = event.dataValues?.some(dv => 
      dv.dataElement === 'I0QtZkMbzxN' || // Task name
      dv.dataElement === 'LR70eqTjB8m' || // Task description
      dv.dataElement === 'AElpNxzTdRh'    // Task progress percent
    );
    
    const hasKraData = event.dataValues?.some(dv => 
      dv.dataElement === 'obapz6Gnyla' || // KRA name
      dv.dataElement === 'DEECWPTj42E' || // KRA description
      dv.dataElement === 'Xl4VEFoIFIw'    // KRA weight
    );
    
    const hasKpiData = event.dataValues?.some(dv => 
      dv.dataElement === 'DBmylDQA9KX' || // KPI name
      dv.dataElement === 'rQqF75V9t7K' || // KPI description
      dv.dataElement === 'Ru68Ax2Xo2L'    // KPI target
    );

    if (hasTaskData || hasKraData || hasKpiData) {
      console.log('✅ Filtering out event with task/KRA/KPI data elements:', event.event, {
        hasTaskData,
        hasKraData,
        hasKpiData
      });
      return null;
    }

    return {
      id: event.event,
      programStage: event.programStage,
      name: event.dataValues?.find((dv) => dv.dataElement === 'EuN5ztFnXsQ')?.value || 'Unnamed',
      description: event.dataValues?.find((dv) => dv.dataElement === 'nWqW5BrcgP1')?.value || '',
      orgUnit: event.orgUnit,
      startDate: event.dataValues?.find((dv) => dv.dataElement === 'eDcVy661j9e')?.value || '',
      completionDate: event.dataValues?.find((dv) => dv.dataElement === 'J0rjxrDKn1l')?.value || '',
      assignedRoleId: userRole,
      username,
      userRole,
      approvalStatus: event.dataValues?.find(dv => dv.dataElement === 'DzQorireRBf')?.value || 'pending',
      approvalRemark: event.dataValues?.find(dv => dv.dataElement === 'KeGWxm1IDYF')?.value || '',
      approvedBy: event.dataValues?.find(dv => dv.dataElement === 'owgP6wkRUwU')?.value || '',
      dataValues: event.dataValues || [],

      // Drag & Drop fields
      objectiveAlignment: event.dataValues?.find(dv => dv.dataElement === 'neuilg3L4d2')?.value || '',
      objectiveRoleAssigned: event.dataValues?.find(dv => dv.dataElement === 'vFoMLF5eZ5X')?.value || '',
      higherLevelObjectiveId: event.dataValues?.find(dv => dv.dataElement === 'plJovUlxfQ0')?.value || '',

      // ✅ KRA and KPI arrays (from separate events)
      kras: kras,
      kpis: kpis,
    }
    
  } catch (error) {
    console.error('Error transforming event:', error);
    return null;
  }
};

// Helper function to fetch linked KRA/KPI events
const fetchLinkedEvents = async (objectiveId, type) => {
  try {
    let programStage = '';
    
    if (type === 'kra') {
      programStage = 'BJb3qTgbcTF'; // KRA program stage
    } else if (type === 'kpi') {
      programStage = 'ivyCLjNLFrd'; // KPI program stage
    }
    
    // ✅ FIXED: Use "events" as the key instead of "linkedEvents"
    const response = await engine.query({
      events: {
        resource: 'events',
        params: {
          program: 'yiT00XKrrHP',
          programStage: programStage,
          filter: [`plJovUlxfQ0:eq:${objectiveId}`],
          fields: 'event,dataValues[dataElement,value]',
          pageSize: 100
        }
      }
    });
    
    // ✅ ADD DEBUGGING
    console.log(`🟡 [DEBUG] ${type.toUpperCase()} response:`, response);
    
    // ✅ FIXED: Correct response parsing
    let events = [];
    if (response.events && Array.isArray(response.events.events)) {
      
      events = response.events.events;
    } else if (Array.isArray(response.events)) {
      // Direct API format: { events: [] }
      events = response.events;
    } else if (Array.isArray(response)) {
      // Array response
      events = response;
    }
    
    console.log(`🔍 [DEBUG] Found ${type.toUpperCase()} events:`, events.length);
    console.log(`🔍 [DEBUG] ${type.toUpperCase()} event IDs:`, events.map(e => e.event));
    
    if (type === 'kra') {
      return events.map(event => ({
        id: event.event,
        programStage: event.programStage,
        name: event.dataValues?.find(dv => dv.dataElement === 'obapz6Gnyla')?.value || '',
        description: event.dataValues?.find(dv => dv.dataElement === 'DEECWPTj42E')?.value || '',
        weight: event.dataValues?.find(dv => dv.dataElement === 'Xl4VEFoIFIw')?.value || '0'
      }));
    } else {
      return events.map(event => ({
        id: event.event,
        programStage: event.programStage,
        name: event.dataValues?.find(dv => dv.dataElement === 'DBmylDQA9KX')?.value || '',
        description: event.dataValues?.find(dv => dv.dataElement === 'rQqF75V9t7K')?.value || '',
        target: event.dataValues?.find(dv => dv.dataElement === 'Ru68Ax2Xo2L')?.value || ''
      }));
    }
  } catch (error) {
    console.error(`Error fetching linked ${type.toUpperCase()}s:`, error);
    return [];
  }
};

// Add minimum loading time to prevent flicker
const [minLoadingTimePassed, setMinLoadingTimePassed] = useState(false);

useEffect(() => {
  const timer = setTimeout(() => {
    setMinLoadingTimePassed(true);
  }, 1000); // Minimum 1 second loading time

  return () => clearTimeout(timer);
}, []);

useEffect(() => {
    let isMounted = true;

    const loadObjectives = async () => {
      // Set both global and local loading states
      setLoading(true);
      setLocalLoading(true);

      try {
        const transformed = await Promise.all(events.map(transformEventToObjective));

        if (isMounted) {
          setObjectivesWithRoles(transformed.filter(obj => obj !== null && obj.username?.toLowerCase() === normalizedLoggedInUser));
        }
      } catch (error) {
        console.error('Error loading objectives:', error);
      } finally {
        if (isMounted) {
          // Use a minimum loading time to prevent flicker
          setTimeout(() => {
            setLoading(false);
            setLocalLoading(false);
          }, 500); // Minimum 500ms loading time
        }
      }
    };

    if (events.length && user?.username) {
      loadObjectives();
    } else {
      setObjectivesWithRoles([]);
      setLoading(false);
      setLocalLoading(false);
    }

    return () => {
      isMounted = false;
      if (loadingTimerRef.current) clearTimeout(loadingTimerRef.current);
    };
  }, [events, user?.username, setLoading, normalizedLoggedInUser]);

   // Combined loading state - show loader if either global or local loading is active
   const isLoading = loadingEvents || localLoading;

   return (
      <div className="performance-list" style={{ padding: '1rem', position: 'relative' }}>
        {/* Full page loader for initial load */}
        {isLoading && !minLoadingTimePassed && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            flexDirection: 'column',
            gap: '16px'
          }}>
            <CircularLoader small/>
            <div style={{ color: '#2c6693', fontSize: '18px', fontWeight: '500' }}>
              Loading Your Objectives...
            </div>
            <div style={{ color: '#666', fontSize: '14px' }}>
              Please wait while we load your performance data
            </div>
          </div>
        )}

        {/* Overlay loader for subsequent loads */}
        {isLoading && minLoadingTimePassed && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            
            gap: '16px'
          }}>
           <CircularLoader small/>
           <div style={{ color: '#2c6693', fontSize: '14px', fontWeight: '500' }}>
            Please wait, this may take a while...
           </div>
          </div>
        )}

        {/* My Objectives Section */}
        {myObjectives.length > 0 && (
          <section style={{
            marginBottom: '2.5rem',
            backgroundColor: '#e9f8e8ff',
            borderRadius: '8px',
            boxShadow: '0 8px 8px rgba(0,0,0,0.1)',
            padding: '1rem',
            opacity: isLoading ? 0.5 : 1,
            pointerEvents: isLoading ? 'none' : 'auto',
          }}>
            <h4 style={{
              color: '#2c6693',
              borderBottom: '2px solid #e6ebf9',
              paddingBottom: '0.5rem',
              marginTop: 0,
              marginBottom: '1rem',
              fontSize: '1.1rem',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center'
            }}>
              <span style={{
                display: 'inline-block',
                width: '12px',
                height: '12px',
                backgroundColor: '#2c6693',
                borderRadius: '50%',
                marginRight: '0.75rem'
              }}></span>
              {isCEO ? 'My CEO Objectives' : 
              isHOD ? 'My Department Objectives' :
              isTopStaff ? 'My Top Staff Objectives' :
              isSeniorStaff ? 'My Senior Staff Objectives' :
              'My Objectives'} ({myObjectives.length})
            </h4>

            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {myObjectives.map((objective) => {
                const isExpanded = expandedObjectives.has(objective.id);

                return (
                  <div
                    key={objective.id}
                    style={{
                      backgroundColor: "#fff",
                      borderRadius: "8px",
                      padding: "16px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      border: "1px solid #e0e0e0",
                      transition: "0.2s ease-in-out",
                    }}
                  >
                    {/* Top row (summary info) */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr 1fr auto",
                        alignItems: "center",
                        gap: "12px",
                        flexWrap: "wrap",
                      }}
                    >
                      <div>
                        <strong style={{ color: "#2c6693" }}>Name:</strong>
                        <div>{objective.name}</div>
                      </div>
                      <div>
                        <strong style={{ color: "#2c6693" }}>Created By:</strong>
                        <div>{objective.username}</div>
                      </div>
                      <div>
                        <strong style={{ color: "#2c6693" }}>Start:</strong>
                        <div>{objective.startDate}</div>
                      </div>
                      <div>
                        <strong style={{ color: "#2c6693" }}>Completion:</strong>
                        <div>{objective.completionDate}</div>
                      </div>

                      {/* Actions */}
                      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                        {objective.approvalStatus === "pending" && (
                          <div
                            title="Pending Approval"
                            style={{
                              backgroundColor: "#FFC107",
                              borderRadius: "50%",
                              width: "28px",
                              height: "28px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <IconClock16 />
                          </div>
                        )}
                        {objective.approvalStatus === "approved" && (
                          <div
                            title="Approved"
                            style={{
                              backgroundColor: "#4CAF50",
                              borderRadius: "50%",
                              width: "28px",
                              height: "28px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#fff",
                            }}
                          >
                            <IconCheckmark16 />
                          </div>
                        )}
                        {objective.approvalStatus === "returned" && (
                          <div
                            title="Returned"
                            style={{
                              backgroundColor: "#87CEEB",
                              borderRadius: "50%",
                              width: "28px",
                              height: "28px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#fff",
                            }}
                          >
                            <IconInfo16 />
                          </div>
                        )}

                        {!reportMode && (
                          <Button
                            small
                            icon={<IconView16 />}
                            onClick={() => toggleExpand(objective.id)}
                            disabled={isLoading}
                            data-print-hidden="true"
                          >
                            {isExpanded ? "Hide" : "View"}
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Expanded details */}
                    {isExpanded && (
                      <div
                        style={{
                          marginTop: "16px",
                          padding: "12px",
                          backgroundColor: "#f9f9f9",
                          borderRadius: "6px",
                          border: "1px solid #e0e0e0",
                        }}
                      >
                        <div>
                          <strong>Objective Role Assigned:</strong>{" "}
                          {objective.objectiveRoleAssigned || "N/A"}
                        </div>
                        <div>
                          <strong>User Role:</strong> {objective.userRole || "N/A"}
                        </div>
                        <div>
                          <strong>Approval Status:</strong>{" "}
                          {objective.approvalStatus || "pending"}
                        </div>
                        <div>
                          <strong>Approval Remark:</strong>{" "}
                          {objective.approvalRemark || "None"}
                        </div>

                        {objective.higherLevelObjectiveId && (
                          <div
                            style={{
                              margin: "10px 0",
                              padding: "10px",
                              backgroundColor: "#e8f4fd",
                              borderRadius: "4px",
                              border: "1px solid #b3d9ff",
                            }}
                          >
                            <strong style={{ color: "#2c6693" }}>
                              Alignment Information:
                            </strong>
                            <br />
                            <strong>Aligned with:</strong>{" "}
                            {objective.higherLevelObjectiveName ||
                              objective.higherLevelObjectiveId}
                            <br />
                            <strong>Role Assigned:</strong> {objective.objectiveRoleAssigned}
                            <br />
                            <strong>Alignment Type:</strong> {objective.objectiveAlignment}
                          </div>
                        )}

                        <div>
                          <strong>Description:</strong> {objective.description || "N/A"}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Empty state when no objectives */}
        {!isLoading && myObjectives.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '3rem',
            color: '#666',
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            border: '1px dashed #ddd'
          }}>
            <p style={{ fontSize: '16px', margin: 0 }}>
              No objectives found for your account.
            </p>
          </div>
        )}
      </div>
    );
}   

export default MyObjectivesList
